import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  TimeInput,
  Select,
  SelectItem,
  addToast
} from "@heroui/react";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { createTranscriptionAsync, editTranscriptionAsync, getAllTranscriptionsAsync } from "../../redux/transcription";
import axios from "axios";
import AppButton from "../common/AppButton";
import { uploadInBlocks } from "../../utils/blockUpload";
import { forgetUpload, pendingUploadFor, rememberUpload } from "../../utils/pendingUploads";

const NewSessionModal = ({ isOpen, onOpenChange, onCreated }) => {
  const dispatch = useDispatch();
  const [fileAudio, setFileAudio] = useState(null);
  const [fileRtf, setFileRtf] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgressAudio, setUploadProgressAudio] = useState(0);
  const [uploadProgressRtf, setUploadProgressRtf] = useState(0);
  const [isDraggingAudio, setIsDraggingAudio] = useState(false);
  const [isDraggingRtf, setIsDraggingRtf] = useState(false);
  const [creating, setCreating] = useState(false);

  const MANDATORY_LANG = "de-DE";
  const LANGUAGE_OPTIONS = [
    { key: "de-DE", label: "Deutsch (DE)" },
    { key: "fr-FR", label: "Französisch (FR)" },
    { key: "nl-NL", label: "Niederländisch (NL)" },
    { key: "en-GB", label: "Englisch (GB)" },
  ];
  const [selectedLangs, setSelectedLangs] = useState(new Set([MANDATORY_LANG]));

  const [formData, setFormData] = useState({
    name: "",
    startDate: new Date(),
  });

  const handleNameChange = (e) => {
    setFormData({ ...formData, name: e.target.value });
  };

  const handleDateChange = (e) => {
    const parts = e.target.value.split("-");
    if (parts.length !== 3) return;
    const [year, month, day] = parts.map((p) => Number(p));
    if ([year, month, day].some((n) => Number.isNaN(n))) return;

    const newDate = new Date(formData.startDate);
    newDate.setFullYear(year);
    newDate.setMonth(month - 1);
    newDate.setDate(day);
    setFormData({ ...formData, startDate: newDate });
  };

  const handleTimeChange = (time) => {
    const newDate = new Date(formData.startDate);
    newDate.setHours(time.hour);
    newDate.setMinutes(time.minute);
    newDate.setSeconds(time.second ?? 0);
    setFormData({ ...formData, startDate: newDate });
  };

  const formatDateInput = (dateObj) => {
    if (!dateObj || !(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().split("T")[0];
  };

  const formatLocalDateTime = (date) => {
    const pad = (n) => (n < 10 ? `0${n}` : n);
    // Keeps your original payload format (with trailing Z)
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}Z`;
  };

  // NEW: Try to parse "YYYYMMDD_[Gremienkürzel]_HH.mm.ss" from file name
  const extractDateTimeFromFilename = (name) => {
    // Strip extension and match
    const base = name.replace(/\.[^/.]+$/, "");
    const m = base.match(/^(\d{4})(\d{2})(\d{2})_[^_]+_(\d{2})\.(\d{2})\.(\d{2})$/);
    if (!m) return null;
    const [, YYYY, MM, DD, hh, mm, ss] = m.map(String);
    const year = Number(YYYY), month = Number(MM), day = Number(DD);
    const hour = Number(hh), minute = Number(mm), second = Number(ss);
    if ([year, month, day, hour, minute, second].some(Number.isNaN)) return null;

    // Construct as local time
    return new Date(year, month - 1, day, hour, minute, second);
  };

  const applyDateTimeFromFilename = () => {
    if (!fileAudio) return;
    const parsed = extractDateTimeFromFilename(fileAudio.name);
    if (!parsed || Number.isNaN(parsed.getTime())) {
      addToast({
        title: "Kein Datum/Zeit gefunden",
        description: "Der Dateiname entspricht nicht dem erwarteten Format: YYYYMMDD_[Gremienkürzel]_HH.mm.ss",
        color: "warning",
      });
      return;
    }
    setFormData((prev) => ({ ...prev, startDate: parsed }));
    addToast({
      title: "Datum/Zeit übernommen",
      description: parsed.toLocaleString(),
      color: "success",
    });
  };

  const handleFileChangeAudio = async (e) => {
    const file = e.target.files[0];
    if (
      !file ||
      (!file.name.toLowerCase().endsWith(".mp3") &&
        !file.name.toLowerCase().endsWith(".wma") &&
        !file.name.toLowerCase().endsWith(".wav"))
    ) {
      addToast({
        title: "Ungültiger Dateityp",
        description: "Bitte laden Sie eine Datei mit der Endung .mp3, .wav oder .wma hoch.",
        color: "danger",
      });
      e.target.value = null;
      return;
    }
    const maxSizeMB = 400;
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(0);
    if (file.size > maxSizeMB * 1024 * 1024) {
      addToast({
        title: "Datei zu groß",
        description: `Die maximale Dateigröße beträgt ${maxSizeMB} MB. Ihre Datei ist ${fileSizeMB} MB.`,
        color: "danger",
      });
      e.target.value = null;
      return;
    }
    addToast({
      title: `Dateigröße: ${fileSizeMB} MB`,
      description: `Maximale Größe: ${maxSizeMB} MB`,
      color: fileSizeMB > maxSizeMB * 0.75 ? "warning" : "default",
    });
    setFileAudio(file);
    // Do NOT auto-parse; button will trigger it.
  };

  const handleFileChangeRtf = (e) => {
    const file = e.target.files[0];
    if (file && !file.name.toLowerCase().endsWith(".rtf")) {
      addToast({
        title: "Ungültiger Dateityp",
        description: "Bitte laden Sie eine Datei mit der Endung .rtf hoch.",
        color: "danger",
      });
      e.target.value = null;
      return;
    }
    setFileRtf(file);
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !fileAudio) {
      addToast({
        title: "Name und Audiodatei sind erforderlich",
        color: "danger",
      });
      return;
    }

    setCreating(true);
    const payload = {
      name: formData.name,
      start: formatLocalDateTime(formData.startDate),
      meeting_type: "manual",
      stage: "Waiting upload",
      languages: JSON.stringify(Array.from(selectedLangs))
    };

    // A session this file was already being uploaded to takes the form's
    // values and continues that upload; otherwise a new session starts.
    const id =
      (await sessionAwaitingFile(fileAudio, payload)) ??
      (await createSession(payload));
    if (!id) {
      setCreating(false);
      return;
    }

    // Without its files there is nothing to transcribe: a failed upload
    // used to start the transcription anyway. The session keeps waiting for
    // its audio instead, and sending the form again continues the upload.
    if (!(await handleUpload(id))) {
      setCreating(false);
      return;
    }
    await startTranscription(id);

    // Refresh the Home page's paginated table. The modal-internal
    // `getAllTranscriptionsAsync` populates a different store key
    // (`transcriptions`) than what Home reads (`paginatedTranscriptions`),
    // so without this callback the user has to manually refresh to see
    // the new session.
    if (typeof onCreated === "function") {
      try {
        await onCreated();
      } catch (refreshErr) {
        console.error("Post-create refresh failed:", refreshErr);
      }
    }

    setCreating(false);
    setFormData({ name: "", startDate: new Date() });
    setFileAudio(null);
    setFileRtf(null);
    setSelectedLangs(new Set([MANDATORY_LANG]));
    setUploadProgressAudio(0);
    setUploadProgressRtf(0);
    setIsDraggingAudio(false);
    setIsDraggingRtf(false);

    onOpenChange(false);
  };

  const createSession = async (payload) => {
    const response = await dispatch(createTranscriptionAsync(payload));
    const id = response.payload?.id;
    if (createTranscriptionAsync.fulfilled.match(response) && id) return id;
    addToast({
      title: "Sitzung konnte nicht angelegt werden",
      description: response.error?.message,
      color: "danger",
    });
    return null;
  };

  // The session an interrupted upload of this file belongs to, as long as
  // it still waits for its audio. It takes the form's current values.
  const sessionAwaitingFile = async (file, payload) => {
    const pendingId = pendingUploadFor(file);
    if (!pendingId) return null;
    try {
      const { data } = await axios.get(`transcriptions_poc_000113_1_0_0/transcription/${pendingId}`);
      if (data?.stage !== "Waiting upload") throw new Error(`stage ${data?.stage}`);
    } catch (err) {
      forgetUpload(file);
      return null;
    }
    await dispatch(
      editTranscriptionAsync({
        id: pendingId,
        payload: { name: payload.name, start: payload.start, languages: payload.languages },
      })
    );
    return pendingId;
  };

  // Returns whether every file reached storage.
  const handleUpload = async (id) => {
    if (!fileAudio) return false;
    setUploading(true);

    try {
      const ext = fileAudio.name.slice(fileAudio.name.lastIndexOf(".")).toLowerCase();
      const resAudio = await axios.get(`transcriptions_poc_000113_1_0_0/transcription/get-url-mp3/${id}${ext}`);
      const { url: urlAudio } = resAudio.data;
      if (!urlAudio) throw new Error("SAS URL for Audio not received");

      // The blob is named after the session, and a session is only reused
      // for the same file, so blocks it already holds are this file's.
      rememberUpload(fileAudio, id);
      await uploadInBlocks(urlAudio, fileAudio, {
        resume: true,
        onProgress: (sent, total) =>
          setUploadProgressAudio(Math.round((sent * 100) / total)),
      });

      // Upload RTF only if provided
      if (fileRtf) {
        const resRtf = await axios.get(`transcriptions_poc_000113_1_0_0/transcription/get-url-rtf/${id}.rtf`);
        const { url: urlRtf } = resRtf.data;
        if (!urlRtf) throw new Error("SAS URL for RTF not received");

        await axios.put(urlRtf, fileRtf, {
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": fileRtf.type,
          },
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadProgressRtf(percent);
          },
        });
      }

      // Persist the blob name on the transcription row so the new Fast
      // Transcription endpoint can resolve the audio without the client
      // having to send file_name in every request. The old batch path
      // didn't store this, which left existing sessions stuck if their
      // file extension isn't .mp3.
      await dispatch(
        editTranscriptionAsync({
          id,
          payload: {
            stage: "Transcription pending",
            file_name: `${id}${ext}`,
            original_file_name: fileAudio.name,
          },
        })
      );

      forgetUpload(fileAudio);
      addToast({ title: "Dateien erfolgreich hochgeladen", color: "success" });
      onOpenChange(false);
      return true;
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadProgressAudio(0);
      setUploadProgressRtf(0);
      addToast({
        title: "Upload unterbrochen",
        description:
          "Die Transkription wurde nicht gestartet. Erneut senden setzt den Upload fort.",
        color: "danger",
      });
      return false;
    } finally {
      setUploading(false);
    }
  };

  const startTranscription = async (id) => {
    try {
      // New Fast Transcription path. Also pass file_name + languages in
      // the body so the backend doesn't depend on the editTranscription
      // DB write having committed by the time the queue worker picks
      // up the task.
      const ext = fileAudio.name.slice(fileAudio.name.lastIndexOf("."));
      await axios.post(
        `transcriptions_poc_000113_1_0_0/transcribe-fast/${id}`,
        {
          file_name: `${id}${ext}`,
          languages: Array.from(selectedLangs),
        }
      );
      dispatch(getAllTranscriptionsAsync());

      addToast({ title: "Transkription gestartet", color: "success" });
    } catch (err) {
      console.error("Start transcription failed:", err);
      // The backend's reason, e.g. that the session is already being
      // transcribed after the form was sent twice.
      addToast({
        title: "Transkription konnte nicht gestartet werden",
        description: err?.response?.data?.error || err?.message || "",
        color: "danger",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="xl" className="h-[90vh] overflow-auto">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Neue Sitzung</ModalHeader>
            <ModalBody className="space-y-4">
              {/* Title */}
              <Input
                label="Name"
                placeholder="Name der Sitzung"
                value={formData.name}
                onChange={handleNameChange}
              />

              {/* Languages multiselect (German always included) */}
              <Select
                label="Sprachen (Deutsch immer enthalten)"
                description="Deutsch (DE) ist immer aktiviert."
                selectionMode="multiple"
                selectedKeys={selectedLangs}
                onSelectionChange={(keys) => {
                  const normalized =
                    keys === "all"
                      ? new Set(LANGUAGE_OPTIONS.map(o => o.key))
                      : new Set(Array.from(keys));
                  normalized.add(MANDATORY_LANG);
                  setSelectedLangs(normalized);
                }}
                className="w-full"
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.key}
                    value={opt.key}
                    isDisabled={opt.key === MANDATORY_LANG}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </Select>

              {/* Audio Upload */}
              <div
                className="relative w-full"
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingAudio(true);
                }}
                onDragLeave={() => setIsDraggingAudio(false)}
                onDrop={async (e) => {
                  e.preventDefault();
                  setIsDraggingAudio(false);
                  const droppedFile = e.dataTransfer.files[0];
                  if (!droppedFile) return;

                  if (!/\.mp3$|\.wma$|\.wav$/i.test(droppedFile.name)) {
                    addToast({
                      title: "Ungültiger Dateityp",
                      description: "Please drop a file with .mp3, .wav or .wma extension.",
                      color: "danger",
                    });
                    return;
                  }

                  await handleFileChangeAudio({ target: { files: [droppedFile] } });
                }}
              >
                <input
                  id="file-upload-audio"
                  type="file"
                  accept=".mp3,.wma,.wav"
                  onChange={handleFileChangeAudio}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload-audio"
                  className={`block w-full h-32 px-6 py-6 border-2 rounded-2xl transition text-gray-700 text-lg font-semibold text-center flex items-center justify-center cursor-pointer ${
                    isDraggingAudio
                      ? "bg-secondaryContainer"
                      : "hover:bg-secondaryContainer bg-surface text:onSecondaryContainer hover:text-onSecondaryContainer"
                  }`}
                >
                  {isDraggingAudio
                    ? "Audio Datei ablegen. 🎵"
                    : fileAudio
                    ? fileAudio.name
                    : "MP3 oder WMA/WAV Datei per Klick oder Drag & Drop hinzufügen."}
                </label>

                <p className="text-xs text-amber-600 mt-1">Maximale Dateigröße: 400 MB</p>

                {uploadProgressAudio > 0 && (
                  <>
                    <div className="text-sm text-gray-600 mt-2">
                      Audio Uploading: {uploadProgressAudio}%
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                      <div className="bg-secondaryContainer h-2.5 rounded-full" style={{ width: `${uploadProgressAudio}%` }}></div>
                    </div>
                  </>
                )}
              </div>

              

              {/* RTF Upload */}
              <div
                className="relative w-full"
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingRtf(true);
                }}
                onDragLeave={() => setIsDraggingRtf(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingRtf(false);
                  const droppedFile = e.dataTransfer.files[0];
                  if (droppedFile) {
                    if (!droppedFile.name.toLowerCase().endsWith(".rtf")) {
                      addToast({
                        title: "Ungültiger Dateityp",
                        description: "Please drop a file with .rtf extension.",
                        color: "danger",
                      });
                      return;
                    }
                    setFileRtf(droppedFile);
                  }
                }}
              >
                <input
                  id="file-upload-rtf"
                  type="file"
                  accept=".rtf"
                  onChange={handleFileChangeRtf}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload-rtf"
                  className={`block w-full h-32 px-6 py-6 border-2 rounded-2xl transition text-gray-700 text-lg font-semibold text-center flex items-center justify-center cursor-pointer ${
                    isDraggingRtf
                      ? "bg-secondaryContainer"
                      : "hover:bg-secondaryContainer bg-surface text:onSecondaryContainer hover:text-onSecondaryContainer"
                  }`}
                >
                  {isDraggingRtf
                    ? "RTF Datei ablegen. 📄"
                    : fileRtf
                    ? fileRtf.name
                    : "RTF Datei per Klick oder Drag & Drop hinzufügen."}
                </label>

                {uploadProgressRtf > 0 && (
                  <>
                    <div className="text-sm text-gray-600 mt-2">
                      RTF Uploading: {uploadProgressRtf}%
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                      <div className="bg-secondaryContainer h-2.5 rounded-full" style={{ width: `${uploadProgressRtf}%` }}></div>
                    </div>
                  </>
                )}
              </div>

              {fileRtf && (
                <div className="space-y-3">
                  <div className="flex gap-3 flex-nowrap">
                    <AppButton
                      width="w-auto"
                      variant="flat"
                      onClick={() => {
                        if (!fileAudio) return;
                        const parsed = extractDateTimeFromFilename(fileAudio.name);
                        if (!parsed || Number.isNaN(parsed.getTime())) {
                          addToast({
                            title: "Kein Datum/Zeit gefunden",
                            description:
                              "Der Dateiname entspricht nicht dem erwarteten Format: YYYYMMDD_[Gremienkürzel]_HH.mm.ss",
                            color: "warning",
                          });
                        } else {
                          setFormData((prev) => ({ ...prev, startDate: parsed }));
                          addToast({
                            title: "Datum/Zeit erfolgreich übernommen",
                            description: `Erkanntes Datum/Zeit (Audio): ${parsed.toLocaleString()}`,
                            color: "success",
                          });
                        }
                      }}
                    >
                      Aus Audio übernehmen
                    </AppButton>
                    {fileRtf && (
                      <AppButton
                        width="w-auto"
                        variant="flat"
                        onClick={() => {
                          if (!fileRtf) return;
                          const parsed = extractDateTimeFromFilename(fileRtf.name);
                          if (!parsed || Number.isNaN(parsed.getTime())) {
                            addToast({
                              title: "Kein Datum/Zeit gefunden",
                              description:
                                "Der RTF-Dateiname entspricht nicht dem erwarteten Format: YYYYMMDD_[Gremienkürzel]_HH.mm.ss",
                              color: "warning",
                            });
                          } else {
                            setFormData((prev) => ({ ...prev, startDate: parsed }));
                            addToast({
                              title: "Datum/Zeit erfolgreich übernommen",
                              description: `Erkanntes Datum/Zeit (RTF): ${parsed.toLocaleString()}`,
                              color: "success",
                            });
                          }
                        }}
                      >
                        Aus RTF übernehmen
                      </AppButton>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <Input
                      label="Datum"
                      type="date"
                      value={formatDateInput(formData.startDate)}
                      onChange={handleDateChange}
                      className="max-w-xs"
                    />
                    <TimeInput
                      granularity="second"
                      hourCycle={24}
                      label="Startzeit"
                      value={{
                        hour: formData.startDate.getHours(),
                        minute: formData.startDate.getMinutes(),
                        second: formData.startDate.getSeconds(),
                      }}
                      onChange={handleTimeChange}
                      className="max-w-xs"
                    />
                  </div>

                  {/* The speaker assignment converts each paragraph's audio
                      offset to a wall-clock time using this value, then
                      matches it against the RTF timestamps. Entering the
                      moment of the first spoken word instead of the start of
                      the recording shifts every speaker by that difference. */}
                  <div className="text-sm text-gray-600 leading-relaxed border-l-2 border-amber-400 pl-3">
                    Bitte die Uhrzeit eintragen, zu der die{" "}
                    <span className="font-semibold">Audioaufnahme gestartet</span>{" "}
                    wurde — <span className="font-semibold">nicht</span> die Uhrzeit,
                    zu der die erste Person zu sprechen beginnt.
                    <br />
                    Die Aufnahme beginnt in der Regel einige Sekunden vor dem ersten
                    Wort. Diese Uhrzeit verbindet die Audiodatei mit den Zeitstempeln
                    der RTF-Datei: ist sie zu spät angesetzt, werden die Wortmeldungen
                    den falschen Sprechern zugeordnet.
                  </div>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="bordered" radius="full" onPress={onClose}>
                Abbrechen
              </Button>
              <AppButton onClick={handleCreate} width="w-[120px]" rounded="rounded-[16px]" disabled={creating}>
                Anlegen
              </AppButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default NewSessionModal;
